import React, { useState } from 'react';
import { ordersAPI } from '../../utils/api';
import { toast } from 'sonner';
import { MessageCircle, ChevronDown, ChevronUp, Truck } from 'lucide-react';

const OrderManagement = ({ orders, onRefresh }) => {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [trackingForm, setTrackingForm] = useState({ tracking_id: '', tracking_link: '' });

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await ordersAPI.updateStatus(orderId, status);
      toast.success('Order status updated');
      onRefresh();
    } catch (_) { toast.error('Failed to update order status'); }
  };

  const handleUpdateTracking = async (orderId) => {
    try {
      await ordersAPI.updateTracking(orderId, trackingForm);
      toast.success('Tracking info updated');
      setTrackingForm({ tracking_id: '', tracking_link: '' });
      onRefresh();
    } catch (_) { toast.error('Failed to update tracking'); }
  };

  const handleSendTrackingWhatsApp = (order) => {
    const trackingInfo = order.tracking_id ? `\nTracking ID: ${order.tracking_id}` : '';
    const trackingLink = order.tracking_link ? `\nTrack here: ${order.tracking_link}` : '';
    const message = encodeURIComponent(
      `Hi ${order.customer_name},\nYour order #${order.id.slice(0, 8)} from Studio Crochet has been ${order.status}.${trackingInfo}${trackingLink}\nThank you for shopping with us!`
    );
    window.open(`https://wa.me/91${order.customer_phone}?text=${message}`, '_blank');
  };

  const toggleOrderDetails = (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
    } else {
      setExpandedOrder(orderId);
      const order = orders.find(o => o.id === orderId);
      if (order) {
        setTrackingForm({ tracking_id: order.tracking_id || '', tracking_link: order.tracking_link || '' });
      }
    }
  };

  return (
    <div data-testid="orders-tab">
      <h2 className="font-cormorant text-2xl mb-6">Orders Management</h2>
      {orders.length === 0 ? (
        <p className="font-outfit text-[#8C8577] text-center py-12">No orders yet</p>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-lg shadow-sm overflow-hidden" data-testid={`order-card-${order.id}`}>
              <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#FCFBF8] transition-colors" onClick={() => toggleOrderDetails(order.id)} data-testid={`order-header-${order.id}`}>
                <div className="flex items-center gap-6">
                  <div><p className="font-outfit text-xs text-[#8C8577]">ORDER</p><p className="font-outfit text-sm font-medium">#{order.id.slice(0, 8)}</p></div>
                  <div><p className="font-outfit text-xs text-[#8C8577]">CUSTOMER</p><p className="font-outfit text-sm">{order.customer_name}</p></div>
                  <div><p className="font-outfit text-xs text-[#8C8577]">TOTAL</p><p className="font-outfit text-sm text-[#C29B57]">₹{order.total?.toLocaleString()}</p></div>
                  <div><p className="font-outfit text-xs text-[#8C8577]">PAYMENT</p><p className="font-outfit text-sm">{order.payment_method}</p></div>
                  <div>
                    <p className="font-outfit text-xs text-[#8C8577]">DATE</p>
                    <p className="font-outfit text-sm">{new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    <p className="font-outfit text-xs text-[#8C8577]">{new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <select value={order.status} onChange={(e) => { e.stopPropagation(); handleUpdateOrderStatus(order.id, e.target.value); }} onClick={(e) => e.stopPropagation()} className="border border-[#EBE4D5] rounded-md px-3 py-1.5 text-sm font-outfit" data-testid={`order-status-${order.id}`}>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  {expandedOrder === order.id ? <ChevronUp className="w-5 h-5 text-[#8C8577]" /> : <ChevronDown className="w-5 h-5 text-[#8C8577]" />}
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="border-t border-[#EBE4D5] p-6 bg-[#FCFBF8]" data-testid={`order-details-${order.id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-outfit text-sm uppercase tracking-wider text-[#8C8577] mb-3">Customer Details</h4>
                      <p className="font-outfit text-sm mb-1"><span className="text-[#8C8577]">Name:</span> {order.customer_name}</p>
                      <p className="font-outfit text-sm mb-1"><span className="text-[#8C8577]">Phone:</span> {order.customer_phone}</p>
                      <p className="font-outfit text-sm mb-1"><span className="text-[#8C8577]">Email:</span> {order.customer_email}</p>
                      <p className="font-outfit text-sm mb-1"><span className="text-[#8C8577]">Address:</span> {order.customer_address}</p>
                      {order.order_notes && <p className="font-outfit text-sm mt-2"><span className="text-[#8C8577]">Notes:</span> <em>{order.order_notes}</em></p>}
                    </div>
                    <div>
                      <h4 className="font-outfit text-sm uppercase tracking-wider text-[#8C8577] mb-3">Items Ordered</h4>
                      <div className="space-y-2">
                        {order.items?.map((item, idx) => (
                          <div key={`${item.product_id}-${item.color}-${idx}`} className="flex justify-between font-outfit text-sm">
                            <span>{item.product_name} x {item.quantity} {item.color ? `(${item.color})` : ''} {item.size ? `- ${item.size}` : ''}</span>
                            <span>₹{(item.price * item.quantity).toLocaleString()}</span>
                          </div>
                        ))}
                        <div className="border-t border-[#EBE4D5] pt-2 flex justify-between font-outfit text-sm font-medium">
                          <span>Total</span><span className="text-[#C29B57]">₹{order.total?.toLocaleString()}</span>
                        </div>
                        {order.payment_method === 'COD' && order.advance_paid > 0 && (
                          <>
                            <div className="flex justify-between font-outfit text-sm text-[#3A5A40]"><span>Advance Paid</span><span>₹{order.advance_paid?.toLocaleString()}</span></div>
                            <div className="flex justify-between font-outfit text-sm text-[#C29B57]"><span>Balance Due</span><span>₹{order.balance_due?.toLocaleString()}</span></div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-[#EBE4D5] pt-4">
                    <h4 className="font-outfit text-sm uppercase tracking-wider text-[#8C8577] mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Tracking Info</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <div>
                        <label className="font-outfit text-xs text-[#8C8577] block mb-1">Tracking ID</label>
                        <input type="text" value={trackingForm.tracking_id} onChange={(e) => setTrackingForm({ ...trackingForm, tracking_id: e.target.value })} placeholder="e.g. AWB123456" className="w-full border border-[#EBE4D5] rounded-md px-3 py-2 text-sm font-outfit" data-testid={`tracking-id-input-${order.id}`} />
                      </div>
                      <div>
                        <label className="font-outfit text-xs text-[#8C8577] block mb-1">Tracking Link</label>
                        <input type="text" value={trackingForm.tracking_link} onChange={(e) => setTrackingForm({ ...trackingForm, tracking_link: e.target.value })} placeholder="https://track.delhivery.com/..." className="w-full border border-[#EBE4D5] rounded-md px-3 py-2 text-sm font-outfit" data-testid={`tracking-link-input-${order.id}`} />
                      </div>
                      <button onClick={() => handleUpdateTracking(order.id)} className="bg-[#C29B57] text-white px-4 py-2 rounded-md text-sm font-outfit hover:bg-[#99733D] transition-colors" data-testid={`save-tracking-${order.id}`}>Save Tracking</button>
                    </div>
                    {order.tracking_id && (
                      <div className="mt-3 p-3 bg-white rounded-md">
                        <p className="font-outfit text-sm"><span className="text-[#8C8577]">Current Tracking:</span> {order.tracking_id}</p>
                        {order.tracking_link && <a href={order.tracking_link} target="_blank" rel="noopener noreferrer" className="font-outfit text-sm text-[#C29B57] hover:underline">{order.tracking_link}</a>}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3 mt-4 pt-4 border-t border-[#EBE4D5]">
                    <button onClick={() => handleSendTrackingWhatsApp(order)} className="bg-[#25D366] text-white px-4 py-2 rounded-md text-sm font-outfit flex items-center gap-2 hover:bg-[#1DA851] transition-colors" data-testid={`whatsapp-send-${order.id}`}>
                      <MessageCircle className="w-4 h-4" /> Send Update via WhatsApp
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
